/**
 * CookieConsentBanner — bottom slide-up banner shown on first visit
 * (or after consent expires / policy version bumps).
 *
 * Portals to document.body so it renders above every layout.
 * Fully keyboard-navigable + ARIA-labelled for WCAG AA compliance.
 */
import { AnimatePresence, motion } from "framer-motion";
import { Cookie, Settings2 } from "lucide-react";
import { Link } from "react-router-dom";
import Portal from "@/components/Portal";
import { useCookieConsent } from "@/contexts/CookieConsentContext";

export default function CookieConsentBanner() {
  const { needsConsent, acceptAll, declineNonEssential, openPreferences, settings } = useCookieConsent();

  const title = settings.cookie_banner_title || "This website uses cookies.";
  const description = settings.cookie_banner_description ||
    "We use cookies to enhance your browsing experience, analyze site traffic, and deliver personalized content. " +
    "By clicking \u201cAccept All\u201d you consent to our use of cookies. You can customize your preferences " +
    "or decline non-essential cookies at any time.";
  const acceptLabel = settings.cookie_accept_label || "Accept All";
  const declineLabel = settings.cookie_decline_label || "Decline Non-Essential";
  const customizeLabel = settings.cookie_customize_label || "Customize Cookies";
  const bgColor = settings.cookie_banner_bg || "";     // optional admin override
  const textColor = settings.cookie_banner_text || ""; // optional admin override

  return (
    <Portal>
      <AnimatePresence>
        {needsConsent && (
          <motion.aside
            role="dialog"
            aria-modal="false"
            aria-labelledby="cookie-consent-title"
            aria-describedby="cookie-consent-desc"
            data-testid="cookie-consent-banner"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-0 bottom-0 z-[80] bg-emerald-darker text-bone border-t-2 border-gold shadow-[0_-8px_32px_rgba(0,0,0,0.35)]"
            style={{
              backgroundColor: bgColor || undefined,
              color: textColor || undefined,
              willChange: "transform, opacity",
            }}
          >
            <div className="container-x py-5 sm:py-6">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-8 items-start">
                <div className="lg:col-span-8 flex gap-3 sm:gap-4">
                  <Cookie className="w-6 h-6 sm:w-7 sm:h-7 text-gold flex-shrink-0 mt-1" aria-hidden />
                  <div className="min-w-0">
                    <h2 id="cookie-consent-title" className="font-serif text-lg sm:text-xl mb-1.5">
                      {title}
                    </h2>
                    <p id="cookie-consent-desc" className="text-sm text-bone/75 leading-relaxed">
                      {description}
                    </p>
                    {/* <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                      <Link
                        to="/p/privacy-policy"
                        data-testid="cookie-privacy-link"
                        className="text-gold hover:text-gold/80 underline underline-offset-2"
                      >
                        Privacy Policy
                      </Link>
                      <Link
                        to="/p/cookie-policy"
                        data-testid="cookie-policy-link"
                        className="text-gold hover:text-gold/80 underline underline-offset-2"
                      >
                        Cookie Policy
                      </Link>
                    </div> */}
                  </div>
                </div>

                <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-2 lg:gap-2.5 lg:items-stretch">
                  <button
                    data-testid="cookie-accept-all"
                    onClick={acceptAll}
                    autoFocus
                    className="px-4 py-2.5 bg-gold hover:bg-gold/90 text-emerald-ink font-medium text-sm transition-colors"
                    style={{ touchAction: "manipulation" }}
                  >
                    {acceptLabel}
                  </button>
                  <button
                    data-testid="cookie-customize"
                    onClick={openPreferences}
                    className="px-4 py-2.5 border border-bone/25 hover:border-gold hover:text-gold text-sm inline-flex items-center justify-center gap-2 transition-colors"
                    style={{ touchAction: "manipulation" }}
                  >
                    <Settings2 className="w-4 h-4" aria-hidden /> {customizeLabel}
                  </button>
                  <button
                    data-testid="cookie-decline"
                    onClick={declineNonEssential}
                    className="px-4 py-2.5 text-bone/60 hover:text-bone text-xs sm:text-sm underline underline-offset-2 transition-colors"
                    style={{ touchAction: "manipulation" }}
                  >
                    {declineLabel}
                  </button>
                </div>
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </Portal>
  );
}